import type { TFunction } from 'i18next';
import { Form } from 'react-bootstrap';
import type { ReservationBoard } from '../../services/reservation-system/reservation-board.service';
import ReservationFormLabel from './ReservationFormLabel';
import RsDetailsToggle from './RsDetailsToggle';
import styles from './ReservationBoardCalendar.module.css';

type RsBookingSidebarNoticeProps = {
  board: ReservationBoard;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  t: TFunction;
};

/** お願い・連絡先（フォーム上部。固定バーに隠れない位置） */
export function RsBookingSidebarNotice({
  board,
  open,
  onOpenChange,
  t,
}: RsBookingSidebarNoticeProps) {
  const hasNoticeOrContact = Boolean(
    (board.settings?.noticeLines && board.settings.noticeLines.length > 0) ||
      board.settings?.contactName ||
      board.settings?.contactPhone
  );
  if (!hasNoticeOrContact) return null;

  return (
    <RsDetailsToggle
      className="mb-2"
      open={open}
      onOpenChange={onOpenChange}
      summary={t('reservation.board.noticeAndContact', {
        defaultValue: 'お願い・連絡先',
      })}
    >
      {board.settings?.noticeLines && board.settings.noticeLines.length > 0 ? (
        <div className="mt-2 small text-muted">
          <p className="mb-1 fw-semibold">
            {t('reservation.board.noticeTitle', { defaultValue: '予約に関するお願い' })}
          </p>
          <ul className="ps-3 mb-0">
            {board.settings.noticeLines.map((line, noticeIndex) => (
              <li key={`${noticeIndex}-${line}`}>{line}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {(board.settings?.contactName || board.settings?.contactPhone) && (
        <div className="mt-2 small">
          <p className="mb-0 fw-semibold">
            {t('reservation.board.contactTitle', { defaultValue: '事故等の連絡先' })}
          </p>
          {board.settings.contactName ? <p className="mb-0">{board.settings.contactName}</p> : null}
          {board.settings.contactPhone ? (
            <p className="mb-0">
              {t('reservation.board.contactPhonePrefix', { defaultValue: 'TEL:' })}{' '}
              {board.settings.contactPhone}
            </p>
          ) : null}
        </div>
      )}
    </RsDetailsToggle>
  );
}

type RsBookingSidebarOptionalProps = {
  notes: string;
  onNotesChange: (value: string) => void;
  deletePassword: string;
  onDeletePasswordChange: (value: string) => void;
  onPersistFormPrefs: () => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  t: TFunction;
};

/** 任意項目（備考・削除パスワード）。主経路の縦を食わない折りたたみ */
export function RsBookingSidebarOptional({
  notes,
  onNotesChange,
  deletePassword,
  onDeletePasswordChange,
  onPersistFormPrefs,
  open,
  onOpenChange,
  t,
}: RsBookingSidebarOptionalProps) {
  const hasOptionalContent = Boolean(notes.trim() || deletePassword);
  return (
    <RsDetailsToggle
      className="mb-2"
      open={open}
      onOpenChange={onOpenChange}
      summary={t('reservation.board.optionalFields', {
        defaultValue: '任意項目（備考・削除パスワード）',
      })}
      badge={
        hasOptionalContent ? (
          <span className={styles.detailsFilledMark}>
            {t('reservation.board.optionalFilled', { defaultValue: '入力あり' })}
          </span>
        ) : null
      }
    >
      <Form.Group className="mb-2 mt-2">
        <ReservationFormLabel
          label={t('reservation.board.notes', { defaultValue: '備考' })}
          optional
          t={t}
        />
        <Form.Control
          as="textarea"
          rows={2}
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
          onBlur={onPersistFormPrefs}
          size="sm"
        />
      </Form.Group>
      <Form.Group className="mb-2">
        <ReservationFormLabel
          label={t('reservation.board.deletePassword', { defaultValue: '削除パスワード' })}
          optional
          t={t}
        />
        <Form.Control
          type="password"
          value={deletePassword}
          onChange={(e) => onDeletePasswordChange(e.target.value)}
          size="sm"
          autoComplete="new-password"
        />
        <Form.Text className="text-muted small">
          {t('reservation.board.deletePasswordHint', {
            defaultValue:
              '設定すると削除時にパスワードが必要です。未設定の場合は誰でも削除できます。',
          })}
        </Form.Text>
      </Form.Group>
    </RsDetailsToggle>
  );
}
